// no imports needed — pure Web Audio API + TypeScript

export type MusicStem = 'calm' | 'combat' | 'endgame';
export type AmbienceLayer = 'wind' | 'birds' | 'ocean';

type StemDef = {
  freq: number;
  type: OscillatorType;
  baseGain: number;
};

type LayerDef = {
  freq: number;
  type: OscillatorType;
  baseGain: number;
};

const STEM_DEFS: Record<MusicStem, StemDef> = {
  calm:    { freq: 220, type: 'sine',     baseGain: 0.03 },
  combat:  { freq: 110, type: 'sawtooth', baseGain: 0.04 },
  endgame: { freq: 165, type: 'triangle', baseGain: 0.05 },
};

const LAYER_DEFS: Record<AmbienceLayer, LayerDef> = {
  wind:  { freq: 80,  type: 'sawtooth', baseGain: 0.04 },
  birds: { freq: 880, type: 'sine',     baseGain: 0.05 },
  ocean: { freq: 55,  type: 'sine',     baseGain: 0.04 },
};

const CROSSFADE_SECS = 2;
const STORM_SIREN_DUCK = 0.28;
const STORM_SIREN_DUCK_SECS = 1.15;

export class FpsAudioMix {
  private ctx: AudioContext | null = null;

  // Master
  private masterGain: GainNode | null = null;
  private masterVolume = 1.0;

  // Stems
  private stemGains: Partial<Record<MusicStem, GainNode>> = {};
  private currentStem: MusicStem = 'calm';

  // Ambience
  private ambienceGains: Partial<Record<AmbienceLayer, GainNode>> = {};
  private ambienceVolumes: Record<AmbienceLayer, number> = { wind: 1, birds: 1, ocean: 1 };

  private initialized = false;
  private stormSirenDuck = 0;
  private stormSirenDuckTimer = 0;

  // ─── AudioContext bootstrap ────────────────────────────────────────────────

  private getCtx(): AudioContext | null {
    if (this.ctx) return this.ctx;
    if (typeof window === 'undefined') return null;
    const w = window as typeof window & { webkitAudioContext?: typeof AudioContext };
    const Ctor = window.AudioContext ?? w.webkitAudioContext;
    if (!Ctor) return null;
    try {
      this.ctx = new Ctor();
    } catch {
      return null;
    }
    return this.ctx;
  }

  private ensureInit(): boolean {
    if (this.initialized) return true;
    const ctx = this.getCtx();
    if (!ctx) return false;

    // Master gain
    this.masterGain = ctx.createGain();
    this.masterGain.gain.value = this.masterVolume;
    this.masterGain.connect(ctx.destination);

    // Music stems
    for (const [name, def] of Object.entries(STEM_DEFS) as [MusicStem, StemDef][]) {
      const gainNode = ctx.createGain();
      gainNode.gain.value = name === this.currentStem ? def.baseGain : 0;
      gainNode.connect(this.masterGain);
      this.stemGains[name] = gainNode;

      const osc = ctx.createOscillator();
      osc.type = def.type;
      osc.frequency.value = def.freq;
      osc.connect(gainNode);
      osc.start();
    }

    // Ambience layers
    for (const [name, def] of Object.entries(LAYER_DEFS) as [AmbienceLayer, LayerDef][]) {
      const vol = this.ambienceVolumes[name];
      const gainNode = ctx.createGain();
      gainNode.gain.value = def.baseGain * vol;
      gainNode.connect(this.masterGain);
      this.ambienceGains[name] = gainNode;

      const osc = ctx.createOscillator();
      osc.type = def.type;
      osc.frequency.value = def.freq;
      osc.connect(gainNode);
      osc.start();
    }

    this.initialized = true;
    return true;
  }

  // ─── Spatial audio ─────────────────────────────────────────────────────────

  createSpatialSource(
    worldPos: { x: number; y: number; z: number },
    listenerPos: { x: number; y: number; z: number },
  ): GainNode | null {
    if (!this.ensureInit()) return null;
    const ctx = this.ctx;
    if (!ctx || !this.masterGain) return null;

    const dx = worldPos.x - listenerPos.x;
    const dy = worldPos.y - listenerPos.y;
    const dz = worldPos.z - listenerPos.z;
    const distance = Math.sqrt(dx * dx + dy * dy + dz * dz);
    const gainValue = Math.max(0, Math.min(1, 1 - distance / 80));

    const gainNode = ctx.createGain();
    gainNode.gain.value = gainValue;
    gainNode.connect(this.masterGain);
    return gainNode;
  }

  // ─── Music stems ───────────────────────────────────────────────────────────

  setStem(stem: MusicStem): void {
    if (!this.ensureInit()) return;
    const ctx = this.ctx;
    if (!ctx) return;

    const t = ctx.currentTime;
    const endT = t + CROSSFADE_SECS;

    for (const [name, def] of Object.entries(STEM_DEFS) as [MusicStem, StemDef][]) {
      const g = this.stemGains[name];
      if (!g) continue;
      const target = name === stem ? def.baseGain : 0;
      g.gain.cancelScheduledValues(t);
      g.gain.setValueAtTime(g.gain.value, t);
      g.gain.linearRampToValueAtTime(target, endT);
    }

    this.currentStem = stem;
  }

  getCurrentStem(): MusicStem {
    return this.currentStem;
  }

  // ─── Ambience layers ───────────────────────────────────────────────────────

  setAmbienceVolume(layer: AmbienceLayer, volume: number): void {
    const clamped = Math.max(0, Math.min(1, volume));
    this.ambienceVolumes[layer] = clamped;

    if (!this.ensureInit()) return;
    const g = this.ambienceGains[layer];
    if (!g) return;
    g.gain.value = LAYER_DEFS[layer].baseGain * clamped;
  }

  getAmbienceVolume(layer: AmbienceLayer): number {
    return this.ambienceVolumes[layer];
  }

  // ─── Master volume ─────────────────────────────────────────────────────────

  setMasterVolume(vol: number): void {
    this.masterVolume = Math.max(0, Math.min(1, vol));
    if (this.masterGain) {
      this.masterGain.gain.value = this.masterVolume;
    }
  }

  getMasterVolume(): number {
    return this.masterVolume;
  }

  /** Lower music stems while the storm shrink siren plays (~1.1s). */
  triggerStormSirenDuck(): void {
    this.stormSirenDuckTimer = STORM_SIREN_DUCK_SECS;
  }

  // ─── Frame update — storm siren duck envelope ────────────────────────────

  update(dt: number): void {
    const target = this.stormSirenDuckTimer > 0 ? STORM_SIREN_DUCK : 1;
    if (this.stormSirenDuckTimer > 0) {
      this.stormSirenDuckTimer = Math.max(0, this.stormSirenDuckTimer - dt);
    }
    const blend = Math.min(1, dt * 10);
    this.stormSirenDuck += (target - this.stormSirenDuck) * blend;
    if (!this.ensureInit()) return;
    const stem = this.stemGains[this.currentStem];
    const def = STEM_DEFS[this.currentStem];
    if (stem) {
      stem.gain.value = def.baseGain * this.stormSirenDuck;
    }
  }

  // ─── Teardown ──────────────────────────────────────────────────────────────

  dispose(): void {
    if (this.ctx) {
      void this.ctx.close().catch(() => { /* ignore */ });
      this.ctx = null;
    }
    this.masterGain = null;
    this.stemGains = {};
    this.ambienceGains = {};
    this.initialized = false;
  }
}
